import { Injectable, TemplateRef } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { FuseConfirmDialogComponent } from '@fuse/components/confirm-dialog/confirm-dialog.component';

import { ComponentType } from '@angular/cdk/portal';
import { ValidatorFn } from '@angular/forms';
import * as _ from 'lodash';
import { Observable, filter, finalize, map } from 'rxjs';
import { AlertDialogComponent } from '../components/alert-dialog/alert-dialog.component';
import { PromptDialogComponent, PromptDialogResult } from '../components/prompt-dialog/prompt-dialog.component';
import {
  PromptNumberDialogComponent,
  PromptNumberDialogResult,
} from '../components/prompt-number-dialog/prompt-number-dialog.component';

export interface IComponentDialogProp {
  width?: string;
  height?: string;
  top?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DialogsService {
  private confirmDialogRef: MatDialogRef<FuseConfirmDialogComponent, boolean>;
  private promptDialogRef: MatDialogRef<PromptDialogComponent, PromptDialogResult>;
  private promptNumberDialogRef: MatDialogRef<PromptNumberDialogComponent, PromptNumberDialogResult>;

  constructor(public dialog: MatDialog) {}

  open<T, D = any, R = any>(
    componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
    config?: MatDialogConfig<D>
  ): MatDialogRef<T, R> {
    return this.dialog.open(componentOrTemplateRef, config);
  }

  confirm(text: string, title?: string): Observable<boolean> {
    this.confirmDialogRef = this.dialog.open(FuseConfirmDialogComponent, {
      disableClose: false,
    });

    this.confirmDialogRef.componentInstance.confirmMessage = text;
    this.confirmDialogRef.componentInstance.titleMessage = title;

    return this.confirmDialogRef.afterClosed().pipe(
      map(res => !!res),
      finalize(() => (this.confirmDialogRef = null))
    );
  }

  alert(message: string, title?: string, buttonCaption?: string): void {
    this.open(AlertDialogComponent, { data: { message, title, buttonCaption }, width: '800px' });
  }

  prompt(title: string, message = '', value = '', placeholder = ''): Observable<PromptDialogResult> {
    this.promptDialogRef = this.dialog.open(PromptDialogComponent, {
      disableClose: true,
      minWidth: '300px',
      maxWidth: '500px',
      panelClass: 'prompt-dialog',
    });

    this.promptDialogRef.componentInstance.title = title;
    this.promptDialogRef.componentInstance.placeholder = placeholder;
    this.promptDialogRef.componentInstance.message = message;
    this.promptDialogRef.componentInstance.value = _.cloneDeep(value);
    return this.promptDialogRef.afterClosed().pipe(finalize(() => (this.promptDialogRef = null)));
  }

  promptNumber(
    title: string,
    message = '',
    value = 0,
    placeholder = '',
    disableSubmitOnFormInvalid = true,
    validators?: ValidatorFn | ValidatorFn[]
  ): Observable<PromptNumberDialogResult> {
    this.promptNumberDialogRef = this.dialog.open(PromptNumberDialogComponent, {
      disableClose: true,
      minWidth: '300px',
      maxWidth: '500px',
      panelClass: 'prompt-number-dialog',
    });

    this.promptNumberDialogRef.componentInstance.title = title;
    this.promptNumberDialogRef.componentInstance.placeholder = placeholder;
    this.promptNumberDialogRef.componentInstance.message = message;
    this.promptNumberDialogRef.componentInstance.value = value;
    this.promptNumberDialogRef.componentInstance.disableSubmitOnFormInvalid = disableSubmitOnFormInvalid;
    this.promptNumberDialogRef.componentInstance.validators = validators;
    return this.promptNumberDialogRef.afterClosed().pipe(finalize(() => (this.promptNumberDialogRef = null)));
  }

  // input dialog

  // yesNo(): Observable<boolean> {
  //   return of(false);
  // }

  component<T, R = any>(
    componentOrTemplateRef: ComponentType<any> | TemplateRef<any>,
    data: null | T = null,
    width = 1200,
    config?: MatDialogConfig
  ): Observable<R> {
    config = config || {};

    if (data) {
      config.data = data;
    }
    if (width) {
      config.width = `${width}px`;
    }

    if ((componentOrTemplateRef as any).getDialogProps !== undefined) {
      const props: IComponentDialogProp = (componentOrTemplateRef as any).getDialogProps();
      if (props.width) {
        config.width = props.width;
      }
      if (props.height) {
        config.height = props.height;
      }
      if (props.top) {
        config.position = {
          top: props.top,
        };
      }
    }

    const dialogRef = this.dialog.open<T, any, R>(componentOrTemplateRef, config);
    return dialogRef.afterClosed().pipe(filter(result => result !== undefined));
  }
}
